import React, { useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSidebar } from '../../contexts/SidebarContext';
import Sidebar from '../../components/ui/Sidebar';
import TopBar from '../../components/ui/TopBar';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Icon from '../../components/AppIcon';
import { CommercialPackagesContent } from '../commercial-packages-management/CommercialPackagesContent';
import { ServicesManagementContent } from '../services-management/ServicesManagementContent';
import { CountryServiceConfigContent } from '../country-service-configuration/CountryServiceConfigContent';

const VALID_TABS = ['packages', 'services', 'countries'];

const AdminPacksServicesPage = () => {
  const { t } = useTranslation();
  const { isCollapsed, toggleSidebar } = useSidebar();
  const [searchParams, setSearchParams] = useSearchParams();

  const activeTab = useMemo(() => {
    const tab = searchParams.get('tab');
    return VALID_TABS.includes(tab) ? tab : 'packages';
  }, [searchParams]);

  useEffect(() => {
    if (!VALID_TABS.includes(searchParams.get('tab'))) {
      setSearchParams({ tab: 'packages' }, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const tabs = [
    { id: 'packages', label: 'Packs commerciaux', icon: 'Package' },
    { id: 'services', label: 'Services', icon: 'Briefcase' },
    { id: 'countries', label: 'Config pays-services', icon: 'Globe' },
  ];

  const breadcrumbItems = [
    { label: t('sidebar.nav.dashboard'), path: '/dashboard', icon: 'LayoutDashboard' },
    {
      label: t('sidebar.nav.administration.packsAndServices'),
      path: '/admin/packs-services',
      icon: 'Package',
    },
  ];

  const renderTabPanel = () => {
    switch (activeTab) {
      case 'services':
        return <ServicesManagementContent />;
      case 'countries':
        return <CountryServiceConfigContent />;
      case 'packages':
      default:
        return <CommercialPackagesContent />;
    }
  };

  return (
    <div className="flex h-screen bg-background overflow-hidden">
      <Sidebar isCollapsed={isCollapsed} onToggle={toggleSidebar} />
      <div className="flex-1 flex flex-col overflow-hidden">
        <TopBar sidebarCollapsed={isCollapsed} onSidebarToggle={toggleSidebar} />
        <main className={`main-content ${isCollapsed ? 'sidebar-collapsed' : ''}`}>
          <div className="container mx-auto px-4 py-6 space-y-6 max-w-7xl">
            <Breadcrumb items={breadcrumbItems} />
            <div>
              <h1 className="text-3xl font-bold text-foreground">
                {t('sidebar.nav.administration.packsAndServices')}
              </h1>
              <p className="text-muted-foreground mt-1">
                Packs commerciaux, catalogue de services et configuration par pays
              </p>
            </div>

            <div className="bg-card border border-border rounded-lg overflow-hidden">
              <div className="flex flex-wrap border-b border-border">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setSearchParams({ tab: tab.id })}
                    className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors ${
                      activeTab === tab.id
                        ? 'bg-primary text-primary-foreground'
                        : 'text-muted-foreground hover:bg-muted/50'
                    }`}
                  >
                    <Icon name={tab.icon} size={18} />
                    {tab.label}
                  </button>
                ))}
              </div>
              <div className="p-4 md:p-6 space-y-6">{renderTabPanel()}</div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default AdminPacksServicesPage;
